import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Sun, Moon, Calculator, ArrowUpRight } from "lucide-react";
import { useTheme } from "../../theme/ThemeContext";
import { NAV_LINKS, SITE } from "../../config/site";

export const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="main-navigation"
      className="fixed inset-x-0 top-0 z-50 border-b border-ink-line bg-ink-bg/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5"
          onClick={() => setOpen(false)}
        >
          <span className="flex h-8 w-8 items-center justify-center bg-accent">
            <Calculator className="h-4.5 w-4.5 text-[#0B0B0B]" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg uppercase tracking-tight text-ink-text">
            MatCalc<span className="text-accent">360</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links-desktop">
          {NAV_LINKS.map((l) =>
            l.href.startsWith("#") ? (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors duration-200 hover:text-accent"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors duration-200 hover:text-accent"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle-button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to high-visibility light mode" : "Switch to dark mode"}
            className="flex h-10 w-10 items-center justify-center border border-ink-line text-ink-text transition-colors duration-200 hover:border-accent hover:text-accent"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <a
            data-testid="nav-cta-button"
            href={SITE.appBaseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 bg-accent px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-[background-color,box-shadow] duration-300 hover:bg-accent-dark hover:shadow-[0_0_24px_rgba(244,123,32,0.4)] sm:flex"
          >
            Try the Calculator
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center border border-ink-line text-ink-text md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-ink-line bg-ink-bg/95 backdrop-blur-xl md:hidden"
        >
          <div className="flex flex-col px-6 py-6">
            {NAV_LINKS.map((l) =>
              l.href.startsWith("#") ? (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-ink-line py-4 font-mono text-sm uppercase tracking-[0.2em] text-ink-text"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-ink-line py-4 font-mono text-sm uppercase tracking-[0.2em] text-ink-text"
                >
                  {l.label}
                </Link>
              )
            )}
            <a
              data-testid="mobile-nav-cta-button"
              href={SITE.appBaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 flex items-center justify-center gap-2 bg-accent px-5 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white"
            >
              Try the Calculator
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
